import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, LogIn, Building } from "lucide-react";
import { SEO } from "../components/SEO";
import { formatCpfCnpj, isValidCpfCnpj } from "../lib/validators";

export const Login = () => {
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!isValidCpfCnpj(cpfCnpj)) {
      setError("Informe um CNPJ ou CPF válido.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          tenant_key: cpfCnpj.replace(/\D/g, ""),
          email,
          password,
        }),
      });

      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Erro ao fazer login.");
      }

      const token = result?.data?.token;
      if (token) {
        localStorage.setItem("token", token);
      }

      window.location.assign("/dashboard");
    } catch (error: any) {
      setError(error.message || "Erro ao fazer login.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-900 font-sans">
      <SEO 
        title="Acesse sua conta | RosTech" 
        description="Faça login no sistema RosTech para gerenciar sua oficina mecânica."
        canonicalUrl="/login"
      />
      
      {/* Left Panel - Login Form */}
      <div className="w-full lg:w-[500px] xl:w-[600px] border-r border-slate-800 bg-[#0f172a] text-slate-300 flex flex-col justify-center px-8 sm:px-12 md:px-16 py-12 relative z-10 flex-shrink-0">
        <Link to="/" className="absolute top-8 left-8 sm:left-12 flex items-center text-sm font-medium text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Link>

        <div className="w-full max-w-sm mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 mb-10">
            <div className="bg-blue-600 text-white p-2 rounded-xl">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                <path d="m9 12 2 2 4-4"/>
              </svg>
            </div>
            <span className="text-3xl font-black tracking-tighter text-white">ROS<span className="text-blue-500">TECH</span></span>
          </Link>

          <h2 className="text-2xl font-bold text-white mb-2">Acesse sua conta</h2>
          <p className="text-sm text-slate-400 mb-8">Faça login abaixo para acessar o sistema.</p>

          <form className="space-y-6" onSubmit={handleLogin}>
            {error && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                {error}
              </div>
            )}
            
            <div className="space-y-1.5">
              <label htmlFor="cpfCnpj" className="block text-sm font-medium text-slate-300">
                CNPJ ou CPF <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building className="h-5 w-5 text-slate-500" />
                </div>
                <input
                  type="text"
                  id="cpfCnpj"
                  className="block w-full pl-10 pr-3 py-2.5 bg-[#1e293b] border border-slate-700 rounded-lg text-white shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm placeholder:text-slate-500 transition-colors"
                  placeholder="00.000.000/0000-00 ou 000.000.000-00"
                  value={cpfCnpj}
                  onChange={(e) => setCpfCnpj(formatCpfCnpj(e.target.value))}
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-sm font-medium text-slate-300">
                E-mail / Usuário <span className="text-rose-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                className="block w-full px-3 py-2.5 bg-[#1e293b] border border-slate-700 rounded-lg text-white shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm placeholder:text-slate-500 transition-colors"
                placeholder="voce@empresa.com.br"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-slate-300">
                  Senha <span className="text-rose-500">*</span>
                </label>
                <a href="#" className="text-sm font-medium text-blue-500 hover:text-blue-400 transition-colors">
                  Esqueceu a senha?
                </a>
              </div>
              <div className="relative">
                <input
                  type="password"
                  id="password"
                  className="block w-full px-3 py-2.5 bg-[#1e293b] border border-slate-700 rounded-lg text-white shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm placeholder:text-slate-500 pr-10 transition-colors"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
                  onClick={() => {
                    const input = document.getElementById('password') as HTMLInputElement;
                    if (input) {
                      input.type = input.type === 'password' ? 'text' : 'password';
                      setShowPassword(!showPassword);
                    }
                  }}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center">
              <input
                id="remember"
                name="remember"
                type="checkbox"
                className="h-4 w-4 bg-[#1e293b] border-slate-700 rounded text-blue-600 focus:ring-blue-500 focus:ring-offset-slate-900"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="remember" className="ml-2 block text-sm text-slate-300">
                Lembrar de mim
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center py-2.5 px-4 mt-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-blue-500 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                "Entrando..."
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Entrar
                </>
              )}
            </button>
          </form>

          <p className="mt-6 text-[11px] text-slate-500 text-center leading-relaxed">
            Ao acessar sua conta, você concorda com nossos{" "}
            <Link to="/termos" className="font-medium text-blue-500 hover:text-blue-400">Termos de Uso</Link>{" "}
            e{" "}
            <Link to="/privacidade" className="font-medium text-blue-500 hover:text-blue-400">Política de Privacidade</Link>.
          </p>

          <div className="mt-8 pt-8 border-t border-slate-800 text-sm text-center">
             <p className="text-slate-500">
               Ainda não tem conta?{' '}
               <Link to="/cadastro" className="font-semibold text-blue-500 hover:text-blue-400">
                 Crie um teste grátis 30 dias
               </Link>
             </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:flex flex-1 relative bg-slate-900">
        <img 
          src="https://images.unsplash.com/photo-1580273916550-e323be2ae537?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" 
          alt="Oficina Mecânica" 
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
        <div className="absolute inset-0 bg-blue-900/20 mix-blend-multiply"></div>
        
        <div className="absolute bottom-12 right-12 text-right">
          <h3 className="text-3xl font-bold text-white mb-2">RosTech Software</h3>
          <p className="text-slate-300">A melhor gestão automotiva da américa latina.</p>
        </div>
      </div>
    </div>
  );
};

